/**
 * Learn more about theme events at
 * https://help.switchthemes.co/baseline/developer/theme-events/overview.html
 */

window.theme = window.theme || {};

window.theme.events = {
  version: '1.0',
};

document.addEventListener('theme:update:cart', async () => {
  document.dispatchEvent(new CustomEvent('baseline:cart:lock'));

  try {
    const response = await fetch(
      `${window.location.pathname}?sections=cart-items,cart-footer,cart-item-count,cart-live-region`
    );
    const data = await response.json();

    if (!data.status) {
      document.body.dispatchEvent(
        new CustomEvent('baseline:cart:update', {
          bubbles: true,
          detail: {
            response: {
              sections: {
                ...data,
              },
            },
          },
        })
      );
    }
  } catch (e) {
    console.error(e);
    document.getElementById('cart-errors').textContent =
      theme.strings.cartError;
    document.dispatchEvent(
      new CustomEvent('theme:cart:error:other', {
        detail: {
          message: theme.strings.cartError,
          error: e,
        },
      })
    );
  } finally {
    document.dispatchEvent(new CustomEvent('baseline:cart:unlock'));
  }
});

/* ==========================================================================
   AKRO - window.refreshCart
   --------------------------------------------------------------------------
   Point d'accroche documente par l'app "Dr Free Gift" : quand elle ajoute ou
   retire le produit cadeau lie a un code promo, elle appelle cette fonction
   au lieu de recharger la page. Le panier deroulant reste donc ouvert et se
   met a jour en place.

   Elle re-rend les 4 sections panier puis emet `baseline:cart:update`,
   l'evenement que CartItems, CartFooter et le store `cartCount` ecoutent
   deja, et renvoie l'objet cart pour que l'appelant lise l'etat a jour.

   Volontairement : aucune ouverture ni fermeture du drawer, aucun scroll,
   aucun focus force. On ne met a jour que les donnees affichees.

   Definie ici parce que theme-events.js est charge en `defer` dans le <head>
   sur toutes les pages, donc avant le script de l'app (charge, lui, sur
   window `load`).

   POURQUOI UN POST cart/update.js ET PAS cart.js + ?sections= :
   la Section Rendering API en GET (`{chemin}?sections=cart-items,...`, ce
   qu'utilise le handler `theme:update:cart` ci-dessus) ne rend PAS les
   sections panier partout. Verifie sur ce thème : sur `/` elle renvoie bien
   les 4 sections, mais sur `/products/...` elle renvoie `{"product": ...}` et
   sur `/collections/...` `{"collection": ...}` - les sections demandees sont
   ignorees. Le drawer ne se mettait donc jamais a jour depuis une page
   produit. Le POST `cart/update.js` avec `sections` + `sections_url` rend les
   4 sections sur TOUTES les pages (/, produit, collection, /cart) et renvoie
   le panier dans la meme reponse : une seule requete, et pas de trou.
   Sans `updates` / `note` / `attributes` / `discount` dans le corps, il ne
   modifie rien - c'est une lecture.
   ========================================================================== */
(() => {
  const CART_SECTIONS = [
    'cart-items',
    'cart-footer',
    'cart-item-count',
    'cart-live-region',
  ];

  /* Si deux rafraichissements se chevauchent, seule la reponse la plus
     recente a le droit de re-rendre le drawer. */
  let generation = 0;

  const cartUpdateUrl = () => {
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    return `${root}cart/update.js`;
  };

  window.refreshCart = async function refreshCart() {
    const ticket = ++generation;

    document.dispatchEvent(new CustomEvent('baseline:cart:lock'));

    try {
      const response = await fetch(cartUpdateUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          sections: CART_SECTIONS.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      const sections = data && data.sections;

      /* Une section manquante ferait planter les listeners du thème, qui lisent
         `sections['cart-item-count']` sans garde. On ne diffuse que si tout est
         la, et que ce refresh est toujours le plus recent. */
      const renderable =
        sections &&
        CART_SECTIONS.every((id) => typeof sections[id] === 'string');

      if (renderable && ticket === generation) {
        document.body.dispatchEvent(
          new CustomEvent('baseline:cart:update', {
            bubbles: true,
            detail: {
              response: {
                sections: {
                  ...sections,
                },
              },
            },
          })
        );
      }

      const { sections: renderedSections, ...cart } = data;
      return cart;
    } catch (e) {
      console.error('[AKRO] refreshCart', e);
      return null;
    } finally {
      document.dispatchEvent(new CustomEvent('baseline:cart:unlock'));
    }
  };
})();

const getCartDrawerEl = () => {
  return document.querySelector('[data-cart-drawer]');
};

document.body.addEventListener('cart-will-open', async () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:opening', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-is-open', async () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:open', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-will-close', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closing', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-is-closed', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closed', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.addEventListener('theme:open:cart-drawer', () => {
  if (
    window.theme.settings.cartType === 'drawer' &&
    Alpine.store('modals').modals.cart
  ) {
    Alpine.store('modals').closeAll();

    Alpine.store('modals').open('cart');
  }
});

document.addEventListener('theme:close:cart-drawer', () => {
  if (
    window.theme.settings.cartType === 'drawer' &&
    Alpine.store('modals').modals.cart
  ) {
    Alpine.store('modals').close('cart');
  }
});

const cartChangeEventHandler = (e) => {
  const { cartItemCount, itemsRootEl, lineItemEl, variantId, key } = e.detail;

  document.dispatchEvent(
    new CustomEvent('theme:cart:change', {
      detail: {
        type: e.type,
        cartItemCount,
        itemsRootEl,
        lineItemEl,
        variantId,
        key,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener('theme:product:add', cartChangeEventHandler);
document.addEventListener('theme:line-item:change', cartChangeEventHandler);

const cartErrorEventHandler = (e) => {
  document.dispatchEvent(
    new CustomEvent('theme:cart:error', {
      detail: {
        type: e.type,
        message: e.detail.message,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener(
  'theme:product:error:add-to-cart',
  cartErrorEventHandler
);

document.addEventListener('theme:line-item:error', cartErrorEventHandler);
document.addEventListener('theme:cart:error:other', cartErrorEventHandler);

/**
 * Set `window.themeEventsDebugMode` to `true` to log events
 * outside the theme editor
 */

if (Shopify.designMode || window.themeEventsDebugMode) {
  const themeEvents = [
    'theme:variant:change',
    'theme:product:add',
    'theme:line-item:change',
    'theme:cart:change',
    'theme:cart:update',
    'theme:cart-drawer:opening',
    'theme:cart-drawer:open',
    'theme:cart-drawer:closing',
    'theme:cart-drawer:closed',
    'theme:product:error:add-to-cart',
    'theme:line-item:error',
    'theme:cart:error:other',
    'theme:cart:error',
  ];

  const aggregateEvents = ['theme:cart:change', 'theme:cart:error'];

  for (const eventName of themeEvents) {
    let color;

    if (eventName.includes('error')) {
      color = '#ef4444';
    } else {
      color = '#22c55e';
    }

    const message = [
      `%c● %c${eventName}`,
      `color: ${color};`,
      'font-weight: bold;',
    ];

    if (aggregateEvents.includes(eventName)) {
      document.addEventListener(eventName, (e) => {
        console.groupCollapsed(...message);
        console.log(
          `Underlying event: %c${e.detail.type}`,
          'font-weight: bold;'
        );
        console.log('Event detail:', e.detail);
        console.groupEnd();
      });
    } else {
      document.addEventListener(eventName, (e) => {
        console.log(...message, e.detail);
      });
    }
  }
}
